/**
 * Shared, referentially-stable "nothing here" values.
 *
 * WHY THIS EXISTS: `items = []`, `options = {}` as a parameter default, or
 * `data ?? []` / `x || []` in a render body, allocate a NEW empty array/object
 * on every render the value is absent. Used as a `useMemo` / `useCallback` /
 * `useEffect` dependency (or handed to a `React.memo` child), that fresh
 * reference makes the memo recompute every render — it compiles, type-checks and
 * reads as correct, and the optimisation silently does nothing. See
 * `scripts/validate-memo-dep-arrays.mjs`.
 *
 * Use these instead of an inline literal wherever the fallback can reach a
 * dependency array:
 *
 *   function Foo({ ids = EMPTY_ARRAY }: { ids?: string[] }) { ... }
 *   const rows = data ?? EMPTY_ARRAY;
 *
 * Both are FROZEN at runtime, so a consumer that mutates one fails loudly
 * instead of corrupting every other consumer's "empty". The static types are
 * deliberately the permissive `never[]` / `Record<string, never>` so they
 * assign to any `T[]` / `Record<string, V>` / all-optional object type without
 * a cast. Do not widen them.
 */

/** A single shared empty array. Assignable to any `T[]`. Never push to it. */
export const EMPTY_ARRAY: never[] = [];
Object.freeze(EMPTY_ARRAY);

/** A single shared empty object. Assignable to any `Record<string, V>`. Never write to it. */
export const EMPTY_OBJECT: Record<string, never> = {};
Object.freeze(EMPTY_OBJECT);
